import { of, from, UnaryFunction } from 'rxjs';
import {
  mergeMap,
  concatMap,
  map,
  filter,
  debounceTime,
  takeUntil,
  repeat,
  catchError,
} from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { initClient } from '@ts-rest/core';
import { DatasetStatus, apiContract } from '@retro-ui/api-contract';

import { Action, createAction } from '../actions';
import { SIGN_IN_FULFILL } from '../authentication/authentication.actions';

import {
  STARTING_MATERIAL_UPLOAD_PART_START,
  STARTING_MATERIAL_UPLOAD_PART_FULFILL,
  STARTING_MATERIAL_UPLOAD_PART_REJECT,
  STARTING_MATERIAL_RESUME_UPLOAD_FULFILL,
  STARTING_MATERIAL_COMPLETE_UPLOAD_FULFILL,
  STARTING_MATERIAL_COMPLETE_UPLOAD_REJECT,
  STARTING_MATERIAL_POLL_START,
  STARTING_MATERIAL_POLL_FULFILL,
  STARTING_MATERIAL_POLL_STOP,
} from './starting-material.actions';
import {
  UploadPartStartActionPayload,
  IdentifiedObject,
  StartingMaterialDatasetItem,
} from './starting-material.types';

const CONCURRENT_PART_UPLOADS = 1;

const baseUrl = `${window.location.origin}/internal`;

const client = initClient(apiContract, {
  baseUrl,
  baseHeaders: {},
});

export function uploadPart(action$: any, state$: any): UnaryFunction<any, any> {
  return action$.pipe(
    ofType(STARTING_MATERIAL_UPLOAD_PART_START),
    mergeMap((action: Action<UploadPartStartActionPayload>) => {
      const { id, uploadId, partNumber, blob, url } = action.payload;

      const isDatasetUnavailable = [DatasetStatus.REMOVED, DatasetStatus.CANCELED].includes(
        state$.value.startingMaterial.dataset.data[id].status
      );

      if (isDatasetUnavailable) {
        return of(createAction(STARTING_MATERIAL_UPLOAD_PART_REJECT, { id }));
      }

      const responsePromise = fetch(url, {
        method: 'PUT',
        body: blob,
      });

      return from(responsePromise).pipe(
        map(response =>
          createAction(STARTING_MATERIAL_UPLOAD_PART_FULFILL, {
            id,
            uploadId,
            partNumber,
            etag: response.headers.get('etag')?.replace(/"/g, ''),
            size: blob.size,
          })
        ),
        catchError(error => of(createAction(STARTING_MATERIAL_UPLOAD_PART_REJECT, error)))
      );
    }, CONCURRENT_PART_UPLOADS), // FIXME: check if this is correct
    catchError(error => of(createAction(STARTING_MATERIAL_UPLOAD_PART_REJECT, error)))
  );
}

export function maybeCompleteUpload(action$: any, state$: any): UnaryFunction<any, any> {
  return action$.pipe(
    ofType(STARTING_MATERIAL_UPLOAD_PART_FULFILL, STARTING_MATERIAL_RESUME_UPLOAD_FULFILL),
    filter((action: Action<IdentifiedObject>) => {
      const { id } = action.payload;
      const { size, uploadedSize } = state$.value.startingMaterial.dataset.data[id];
      return size === uploadedSize;
    }),
    mergeMap((action: Action<IdentifiedObject>) => {
      const { id } = action.payload;
      const { uploadId, parts } = state$.value.startingMaterial.dataset.data[id];

      const apiCall = client.startingMaterials.completeDatasetUpload({
        params: { id: id },
        body: {
          uploadId,
          parts: Object.entries(parts).map(([partNumber, etag]) => ({
            partNumber: Number(partNumber),
            etag: etag as string,
          })),
        },
      });

      return from(apiCall).pipe(
        map(result => createAction(STARTING_MATERIAL_COMPLETE_UPLOAD_FULFILL, result.body)),
        catchError(error => of(createAction(STARTING_MATERIAL_COMPLETE_UPLOAD_REJECT, error)))
      );
    }),
    catchError(error => of(createAction(STARTING_MATERIAL_COMPLETE_UPLOAD_REJECT, error)))
  );
}

export function startPoll(action$) {
  return action$.pipe(
    ofType(SIGN_IN_FULFILL, STARTING_MATERIAL_COMPLETE_UPLOAD_FULFILL),
    map(() => createAction(STARTING_MATERIAL_POLL_START))
  );
}

export function updateActiveUploads(action$) {
  const timeframe = 30 * 60 * 1000; // 30 min

  return action$.pipe(
    ofType(STARTING_MATERIAL_POLL_START),
    concatMap(async () => {
      const resultPrivate = await client.startingMaterials.fetchPrivateDatasets({
        query: {
          where: {
            uploadedAt: {
              gt: new Date(Date.now() - timeframe),
            },
          },
        },
      });
      return resultPrivate.status !== 200 ? [] : resultPrivate.body.data;
    }),
    mergeMap((result: StartingMaterialDatasetItem[]) => {
      const isAnyUploadActive = result.some(item =>
        [DatasetStatus.SENDING, DatasetStatus.PROCESSING].includes(item.status)
      );

      const fulfillmentAction = createAction(STARTING_MATERIAL_POLL_FULFILL, result);
      const stopAction = createAction(STARTING_MATERIAL_POLL_STOP);

      if (!isAnyUploadActive) {
        return of(fulfillmentAction, stopAction);
      }

      return of(fulfillmentAction);
    }),
    catchError(() => of(createAction(STARTING_MATERIAL_POLL_FULFILL)))
  );
}

export function poll(action$) {
  return action$.pipe(
    ofType(STARTING_MATERIAL_POLL_FULFILL),
    debounceTime(3000),
    map(() => createAction(STARTING_MATERIAL_POLL_START)),
    catchError(() => of(createAction(STARTING_MATERIAL_POLL_START))),
    takeUntil(action$.pipe(ofType(STARTING_MATERIAL_POLL_STOP))),
    repeat()
  );
}
